import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { z } from "../zod.js";
import {
  ChecklistManualSetRequest,
  ChecklistWaiveRequest,
  GoLiveResponse,
  ImportCommitRequest,
  ImportCreateRequest,
  ImportCreateResponse,
  ImportJob,
  ImportJobListResponse,
  ImportMappingPatchRequest,
  ImportRowListResponse,
  OnboardingChecklistResponse,
  OnboardingSession,
  OnboardingSessionPatchRequest,
  OnboardingSessionResponse,
  OnboardingStepPayloadPutRequest,
  OnboardingStepState,
  PackInstallRequest,
  PackUpdatePreview,
  PackUpdateRequest,
  PlantMapCommitRequest,
  PlantMapCommitResponse,
  QrKitCreateRequest,
  QrPrintBatch,
  QrPrintBatchListResponse,
  QrPrintItemListResponse,
  TerminologyEntry,
  TerminologyListResponse,
  TerminologyLocale,
  TerminologyUpsertRequest,
  VerticalPackDetail,
  VerticalPackInstall,
  VerticalPackInstallCurrentResponse,
  VerticalPackListWrapper,
} from "./schemas.js";

// Module 02 — onboarding HTTP surface, PHASE 2a slice (spec 02 §5): the wizard
// session state machine + the terminology services. Mirrors catalogue/register.ts
// (the `route()` helper; Idempotency-Key on every mutation; RFC 9457 problem
// responses). The remaining §5 rows — verticals, imports, plant-map, qr-kits,
// checklist/go-live — landed with their slices and are registered below,
// including the QR print-kit rows (§5 /v1/qr-kits) from the D-055 print slice.
//
// THE ROUTE↔SURFACE RECONCILE, accurately (corrected 2026-08-02, four-slice
// audit). This header used to say a controller-less route here would "break the
// route↔surface reconcile". It would NOT have:
// apps/api/test/integration/permission-guard.int.test.ts compared live Nest
// routes against the hand-written testing manifests and nothing else, so a
// route registered here with no controller — or a controller route missing from
// this file — passed CI in silence, while doctrine rule 3 ("the API is the
// product") rested on an agreement nothing checked. That reconcile now derives
// a THIRD set from `generateOpenApiDocument()` and asserts live == manifest ==
// contract, both directions. The claim is now true; keep it that way.

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "put" | "delete";
  path: string;
  operationId: string;
  summary: string;
  permission?: string;
  idempotent?: boolean;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description?: string;
}): void {
  const mutation = config.method !== "get";
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["onboarding"],
    summary: config.summary,
    description:
      (config.description ?? "") +
      (config.permission !== undefined ? ` Requires \`${config.permission}\`.` : ""),
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation && config.idempotent !== false ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});

const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});

// step_no is a path segment (1..5 — widened 2026-08-23, D-214 step 5); coerced from the string param.
const StepNoParam = z.object({
  step_no: z.coerce.number().int().min(1).max(5).openapi({ example: 1 }),
});
// Plain-string path param (the controller validates against the frozen 61-key
// enum via ZodPipe) — avoids emitting the registered TerminologyKey component as
// a path parameter $ref.
const TermKeyParam = z.object({ term_key: z.string() });

// ===========================================================================
// wizard session + steps (spec 02 §2.1, §2.2, §3.1, §5, §9.1)
// ===========================================================================

route({
  method: "get",
  path: "/v1/onboarding/session",
  operationId: "getOnboardingSession",
  summary: "Fetch (auto-create on first call) the tenant onboarding session + step states",
  permission: "onboarding.view",
  description:
    "Resolve-or-create the single per-tenant session (UNIQUE(tenant_id)); returns the session, all four step states, and the checklist summary (score + blocking_unmet).",
  responses: ok("Session + steps + checklist summary", OnboardingSessionResponse),
});
route({
  method: "patch",
  path: "/v1/onboarding/session",
  operationId: "patchOnboardingSession",
  summary: "Update session-level fields (target_go_live_date)",
  permission: "onboarding.manage_wizard",
  request: jsonBody(OnboardingSessionPatchRequest),
  responses: {
    ...ok("Updated session", OnboardingSession),
    409: problemResponse("ALREADY_LIVE — the session is live and read-only"),
  },
});
route({
  method: "put",
  path: "/v1/onboarding/steps/{step_no}/payload",
  operationId: "putOnboardingStepPayload",
  summary: "Save a step's scratch payload with optimistic concurrency (spec 02 §9.1)",
  permission: "onboarding.manage_wizard",
  description:
    "Writes the step's jsonb payload. `updated_at` is the optimistic-concurrency token: a stale value → 409 STALE_PAYLOAD carrying the server copy. First save on a step may omit it. Advances the session draft→in_progress on first progress, or ready_for_review→in_progress on reopen.",
  request: { params: StepNoParam, ...jsonBody(OnboardingStepPayloadPutRequest) },
  responses: {
    ...ok("Updated step state", OnboardingStepState),
    409: problemResponse(
      "STALE_PAYLOAD — updated_at token mismatch; server copy returned / ALREADY_LIVE",
    ),
  },
});
route({
  method: "post",
  path: "/v1/onboarding/steps/{step_no}/complete",
  operationId: "completeOnboardingStep",
  summary: "Mark a step completed (validates §3.1 preconditions)",
  permission: "onboarding.manage_wizard",
  description:
    "Runs the step's §3.1 preconditions (a vertical pack must be installed) → 422 PRECONDITIONS_UNMET with the unmet keys when they fail; advances the step to completed, promotes the session to ready_for_review once all four steps are complete, and emits onboarding.step_completed.",
  request: { params: StepNoParam },
  responses: {
    ...ok("Updated step state", OnboardingStepState),
    409: problemResponse("ALREADY_LIVE — the session is live and read-only"),
    422: problemResponse("PRECONDITIONS_UNMET — extensions.unmet[] lists the failed preconditions"),
  },
});

// ===========================================================================
// plant map — step 3 commit (spec 02 §3.4, §5, §9.1)
// ===========================================================================

route({
  method: "post",
  path: "/v1/onboarding/plant-map/commit",
  operationId: "commitPlantMap",
  summary: "Diff + commit the step-3 draft plant tree through the owning modules",
  permission: "onboarding.manage_wizard",
  description:
    "Diffs the draft plant tree (facilities → departments → work centers, each keyed by a stable code) against what exists in the tenant and CREATEs new nodes / UPDATEs changed ones THROUGH Module 16 (identity & tenancy) — never a direct table write. Parents commit before children. Serialised per tenant by a Postgres advisory lock (§9.1); idempotency is by code-adoption (re-committing the same draft is a no-op reconcile — no duplicates), not by Idempotency-Key replay. storage_locations (Module 06) and machines (Module 08) are unbound ports here: recorded in `pending[]` naming the owning module, never faked. Deletions are never implicit; with `confirm_deletions=true` a node absent from the draft is ARCHIVED through Module 16 (children-first, counted in `deleted`) when unreferenced, or refused → 409 ENTITY_IN_USE (extensions.refused[], no writes) when it still has active children or members. A draft with duplicate codes is rejected up front → 422 DUPLICATE_CODE before any write.",
  request: jsonBody(PlantMapCommitRequest),
  responses: {
    ...ok("Diff result: created/updated/deleted counts, pending[]", PlantMapCommitResponse),
    409: problemResponse(
      "ENTITY_IN_USE — a confirmed deletion targets a still-referenced node (extensions.refused[]) / ALREADY_LIVE — the session is live and read-only",
    ),
    422: problemResponse(
      "DUPLICATE_CODE — the draft repeats a facility/department/work-center code",
    ),
  },
});

// ===========================================================================
// terminology (spec 02 §2.5, §5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/terminology",
  operationId: "getTerminology",
  summary: "Effective terminology for a locale (tenant → pack → base merged)",
  description:
    "Resolves every one of the 61 canonical term keys for the requested locale in the spec 02 §2.5 order (tenant override → pack override → base string); a missing locale row falls back to the base string of the REQUESTED locale, never another locale. Readable by any authenticated principal (spec 02 §5) — the floor PWA consumes it, so it is session-gated, not onboarding.view-gated.",
  request: { query: z.object({ locale: TerminologyLocale.optional() }) },
  responses: ok("Effective term labels", TerminologyListResponse),
});
route({
  method: "put",
  path: "/v1/terminology/{term_key}",
  operationId: "putTerminologyOverride",
  summary: "Upsert a tenant terminology override (one locale per call)",
  permission: "onboarding.edit_terminology",
  description:
    "Upserts the tenant override for (term_key, locale); term_key must be in the frozen 61-key catalog. Emits onboarding.terminology_updated (source='tenant').",
  request: { params: TermKeyParam, ...jsonBody(TerminologyUpsertRequest) },
  responses: ok("The upserted effective override", TerminologyEntry),
});
route({
  method: "delete",
  path: "/v1/terminology/{term_key}",
  operationId: "deleteTerminologyOverride",
  summary: "Reset a term to the pack/base value (removes the tenant override)",
  permission: "onboarding.edit_terminology",
  description:
    "Removes the tenant override for (term_key, locale) and returns the now-effective label (pack or base). Emits onboarding.terminology_updated with the reverted source.",
  request: { params: TermKeyParam, query: z.object({ locale: TerminologyLocale }) },
  responses: ok("The now-effective label after reset", TerminologyEntry),
});

// ===========================================================================
// import pipeline (spec 02 §2.6, §2.7, §3.3, §5) — the generic staging engine
// shared by steps 2 (catalogue) and 4 (opening state). Routes are gated by
// onboarding.view; the fine-grained per-kind import lane (§7/D-034) is enforced
// service-side from the job kind. Parse + commit are async (202 → poll GET);
// validate is synchronous (200).
// ===========================================================================

const IdParam = z.object({ id: z.guid() });

/** The customers-lane narrowing every door that loads a job by id applies
 * (spec 02 §7, D-325 ruling 6), stated once and appended to each. */
const CUSTOMERS_NARROWING =
  "Since P3-E2 PR E2-2c (spec 02 §7, D-325 ruling 6) a principal whose only lineage holding `onboarding.import.customers` is sales reads and acts only on the `customers` and `open_sales_orders` jobs it created, so another user's job of those two kinds answers 404 IMPORT_JOB_NOT_FOUND here, the answer another tenant's job gets.";
const created202 = (description: string, schema: z.ZodType) => ({
  202: { description, content: { "application/json": { schema } } },
});

route({
  method: "post",
  path: "/v1/imports",
  operationId: "createImport",
  summary: "Create an import job + presigned S3 upload URL",
  permission: "onboarding.view",
  description:
    "Creates the import_jobs row (status 'uploaded') and returns it plus a presigned S3 PUT (15-min expiry). Idempotent on (tenant, Idempotency-Key). A retry returns the same job with a fresh presigned PUT, only to the user who created the job and only for the same kind, facility, file and duplicate policy (D-303). Any other caller who presents the key, and that user acting through an impersonation grant, gets 409 `IDEMPOTENCY_KEY_REUSE` with nothing from the job. Since P3-E2 PR E2-2c (D-333 ruling 5) the retry mints a PUT only while the job is still `uploaded` and its upload is not owed deletion; once the job has been parsed, or its upload's deletion was requested (D-332 ruling 3), the retry answers 409 `IMPORT_STATE_INVALID` and no URL, so a deleted upload cannot be put back. The per-kind import lane (spec 02 §7) is enforced service-side; facility-scoped kinds (opening_stock, machines) require facility_id.",
  request: jsonBody(ImportCreateRequest),
  responses: {
    201: {
      description: "The created job + upload_url",
      content: { "application/json": { schema: ImportCreateResponse } },
    },
    403: problemResponse("PERMISSION_DENIED — missing the kind's onboarding.import.* lane"),
    409: problemResponse(
      "IDEMPOTENCY_KEY_REUSE — the key belongs to another caller or another request (D-303); IMPORT_STATE_INVALID — the creator's retry of a job that has left `uploaded` or whose upload is owed deletion (D-333 ruling 5)",
    ),
    422: problemResponse("FACILITY_REQUIRED — facility-scoped kind without facility_id"),
  },
});
route({
  method: "get",
  path: "/v1/imports",
  operationId: "listImports",
  summary: "List import jobs (cursor page; kind/status/facility/date filters)",
  permission: "onboarding.view",
  description:
    "Since P3-E2 PR E2-2c (spec 02 §7, D-325 ruling 6) a principal whose only lineage holding `onboarding.import.customers` is sales is not listed the `customers` and `open_sales_orders` jobs that others created; jobs of every other kind keep their gates.",
  request: {
    query: z.object({
      kind: z.string().optional(),
      status: z.string().optional(),
      facility_id: z.guid().optional(),
      created_from: z.string().optional(),
      created_to: z.string().optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: ok("A page of jobs", ImportJobListResponse),
});
route({
  method: "get",
  path: "/v1/imports/{id}",
  operationId: "getImport",
  summary: "Import job detail (counters + error-report URL)",
  permission: "onboarding.view",
  description: `Cross-tenant id → 404 IMPORT_JOB_NOT_FOUND (id oracle avoidance, spec 02 §9.10). ${CUSTOMERS_NARROWING} \`error_report_url\` is a presigned GET of the stored report for every kind but \`customers\`, and null when the job has no stored report. A \`customers\` job's report is never stored (D-332 ruling 2): its \`error_report_url\` is the API path \`/v1/imports/{id}/error-report\` while the job has error rows, and null otherwise, and that route renders the workbook for each reader with the columns the reader is withheld left out.`,
  request: { params: IdParam },
  responses: {
    ...ok("The job", ImportJob),
    404: problemResponse("IMPORT_JOB_NOT_FOUND"),
  },
});
route({
  method: "post",
  path: "/v1/imports/{id}/parse",
  operationId: "parseImport",
  summary: "Start parsing the uploaded file (async worker)",
  permission: "onboarding.view",
  description:
    "Flips the job to 'parsing'; the worker downloads + stream-parses the .xlsx/.csv (Windows-1251/UTF-8 auto-transcode, 1,000,000-row cap D-025), writes import_job_rows with detected headers, and moves to 'mapping'. " +
    CUSTOMERS_NARROWING,
  request: { params: IdParam },
  responses: {
    ...created202("Accepted; poll GET for progress", ImportJob),
    404: problemResponse("IMPORT_JOB_NOT_FOUND"),
    409: problemResponse("IMPORT_STATE_INVALID — job is not in 'uploaded'"),
  },
});
route({
  method: "patch",
  path: "/v1/imports/{id}/mapping",
  operationId: "setImportMapping",
  summary: "Set/confirm the column→field mapping",
  permission: "onboarding.view",
  description:
    "Since P3-E2 PR E2-2c (D-333 ruling 7) only the CREATOR of a `customers` or `open_sales_orders` job may change its mapping, and anyone else holding the lane is refused 403. The job validates and commits as its creator whoever presses (spec 09 §3.15, D-325 ruling 2), so a mapping another holder wrote would run under the creator's grants. " +
    CUSTOMERS_NARROWING,
  request: { params: IdParam, ...jsonBody(ImportMappingPatchRequest) },
  responses: {
    ...ok("The job with the mapping applied", ImportJob),
    403: problemResponse(
      "PERMISSION_DENIED — the kind's import lane is missing, or the caller is not the creator of a `customers` or `open_sales_orders` job (D-333 ruling 7)",
    ),
    404: problemResponse("IMPORT_JOB_NOT_FOUND"),
    409: problemResponse("IMPORT_STATE_INVALID"),
  },
});
route({
  method: "post",
  path: "/v1/imports/{id}/validate",
  operationId: "validateImport",
  summary: "Validate rows against the kind's validator (synchronous)",
  permission: "onboarding.view",
  description:
    "Dispatches each row to the kind's validator (catalogue kinds → CatalogueImportService); grouped kinds (boms/routings) get whole-group poisoning. Persists per-row status + messages, rolls up counters, writes an error-report .xlsx when there are errors, and moves the job to 'validated'. A kind whose lane is not bound → 422 IMPORT_KIND_NOT_AVAILABLE. Since P3-E2 PR E2-2c the `customers` lane is bound (module 09's `CustomersImportHandler` in the staged import slot, spec 02 §3.3 and spec 09 §3.15), and `open_sales_orders`, `suppliers`, `open_purchase_orders`, `machines` and `people` keep the 422. A `customers` job is judged under its CREATOR's rebuilt context whoever presses validate: rows read `warning` for a warning and no error, carry their `action`, and are judged on their keys here rather than at commit (D-325 ruling 4). Before any row is judged, a `customers` job whose creator no longer holds `onboarding.import.customers`, `sales.manage_customers` or a customer write lineage answers 422 IMPORT_IMPORTER_NOT_PERMITTED and the job does not change (D-325 ruling 2). A `customers` job's report is never stored, and the answer's `error_report_url` names `/v1/imports/{id}/error-report` while there are error rows (D-332 ruling 2). " +
    CUSTOMERS_NARROWING,
  request: { params: IdParam },
  responses: {
    ...ok("The validated job (counters updated)", ImportJob),
    404: problemResponse("IMPORT_JOB_NOT_FOUND"),
    409: problemResponse("IMPORT_STATE_INVALID"),
    422: problemResponse(
      "MAPPING_INCOMPLETE / IMPORT_KIND_NOT_AVAILABLE / IMPORT_IMPORTER_NOT_PERMITTED (a `customers` or `open_sales_orders` job whose creator lost the lane's codes, P3-E2 PR E2-2c and P3-G G-4)",
    ),
  },
});
route({
  method: "get",
  path: "/v1/imports/{id}/rows",
  operationId: "listImportRows",
  summary: "Row grid with validation messages (money/PII redacted per role)",
  permission: "onboarding.view",
  description:
    "Cursor page of rows. Money fields (value_minor/unit_cost_minor/price_minor) are nulled for roles without cost visibility, and `people` rows null email/phone for non-admins; the envelope reports redacted_fields[] (spec 02 §7). Since P3-E2 PR E2-2c a `customers` row's `credit_limit` cell is nulled for a reader spec 09 §7 withholds the credit limit from (the lane's `withheldFields`, D-325 ruling 6), and so is every source cell under a header that is mapped onto it, that names it (its normalized form or a synonym), or that the job's mapping does not map onto a field the reader may see, such as the book export's `credit_limit_minor` (D-333 ruling 2); the free-text filter does not search a withheld cell. A row may carry `action` and warning messages (D-325 ruling 4). " +
    CUSTOMERS_NARROWING,
  request: {
    params: IdParam,
    query: z.object({
      status: z.string().optional(),
      q: z.string().optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: ok("A page of rows + redacted_fields", ImportRowListResponse),
});
route({
  method: "post",
  path: "/v1/imports/{id}/commit",
  operationId: "commitImport",
  summary: "Commit validated rows through the kind's committer (async worker)",
  permission: "onboarding.view",
  description:
    "Blocked with 422 HAS_ERRORS unless allow_partial=true (then error rows → skipped). Flips the job to 'committing'; the worker dispatches to the kind committer (grouped kinds commit whole-group in one txn), records counts, emits onboarding.import_committed, and moves to 'committed'. Idempotent under retry — a worker kill mid-commit never double-applies (natural-key upsert; committed rows excluded on resume). A kind whose lane is not bound → 422 IMPORT_KIND_NOT_AVAILABLE. Since P3-E2 PR E2-2c a `customers` job commits each row in its own transaction through module 09's `CustomersService` as the job's CREATOR, rebuilt from the creator's grants (spec 09 §3.15, D-325 ruling 2), and a resumed or doubled commit adopts the customer a row already created (spec 09 §2.34). Before the commit is enqueued, a `customers` job whose creator no longer holds `onboarding.import.customers`, `sales.manage_customers` or a customer write lineage answers 422 IMPORT_IMPORTER_NOT_PERMITTED and the job does not change; the worker checks again when the commit starts and, on a demoted creator, commits no row and writes that code on every committable row. " +
    CUSTOMERS_NARROWING,
  request: { params: IdParam, ...jsonBody(ImportCommitRequest) },
  responses: {
    ...created202("Accepted; poll GET for progress", ImportJob),
    404: problemResponse("IMPORT_JOB_NOT_FOUND"),
    409: problemResponse("IMPORT_STATE_INVALID"),
    422: problemResponse(
      "HAS_ERRORS / IMPORT_KIND_NOT_AVAILABLE / IMPORT_IMPORTER_NOT_PERMITTED (a `customers` or `open_sales_orders` job whose creator lost the lane's codes, P3-E2 PR E2-2c and P3-G G-4)",
    ),
  },
});
route({
  method: "post",
  path: "/v1/imports/{id}/discard",
  operationId: "discardImport",
  summary: "Discard a job before commit",
  permission: "onboarding.view",
  description:
    "Since P3-E2 PR E2-2c a `customers` job's uploaded file is deleted once the job is discarded or committed: the transition stamps a durable delete request that the import worker's poll carries out and records, and a redaction of a note whose words a staged `customers` row holds requests the same deletion and empties that cell (D-332 ruling 3). " +
    CUSTOMERS_NARROWING,
  request: { params: IdParam },
  responses: {
    ...ok("The discarded job", ImportJob),
    404: problemResponse("IMPORT_JOB_NOT_FOUND"),
    409: problemResponse("IMPORT_STATE_INVALID — already committed/committing/discarded"),
  },
});
route({
  method: "get",
  path: "/v1/imports/{id}/error-report",
  operationId: "getImportErrorReport",
  summary: "The job's error report, rendered now for the reader (.xlsx)",
  permission: "onboarding.view",
  description: `P3-E2 PR E2-2c, D-332 ruling 2 (which answered spec 09 §12 question 16 with the per-reader report). For a \`customers\` job only. Renders the error report on demand from the job's CURRENT rows and stores nothing: every row of the job with its original columns, \`__row_status\` and \`__messages\`, the layout of the stored report other kinds keep (spec 02 §3.3 step 4). Every column the reader is withheld is left out: the \`credit_limit\` column and every header mapped onto it or naming it, and, for a reader spec 09 §7 withholds the credit limit from, every header the job's mapping does not map onto a field that reader may see, such as the book export's \`credit_limit_minor\` (D-333 ruling 2). A cell a later write emptied (a redacted note's words, D-332 ruling 3) reads empty. A job of any other kind answers 404, because its report is the stored file behind its presigned \`error_report_url\`, and so does a \`customers\` job with no error row, which has no report. Served as an attachment with \`Cache-Control: no-store\`. The gates are \`GET /v1/imports/{id}\`'s: tenant, facility, the kind's import lane (403 without it), and the customers-lane narrowing. ${CUSTOMERS_NARROWING}`,
  request: { params: IdParam },
  responses: {
    200: {
      description: "The error report as an .xlsx workbook, attachment disposition",
      content: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
          schema: z.string().openapi({ format: "binary" }),
        },
      },
    },
    403: problemResponse("PERMISSION_DENIED — missing the kind's onboarding.import.* lane"),
    404: problemResponse(
      "IMPORT_JOB_NOT_FOUND — a cross-tenant id, a job the customers-lane narrowing hides, a job of any kind but `customers`, and a `customers` job with no error row",
    ),
  },
});

// ===========================================================================
// vertical packs (spec 02 §2.3, §2.11, §3.2, §5) — pack listing/preview +
// install. Listing/preview is platform-global content, still session-gated via
// onboarding.view (any office role, §7); install requires onboarding.install_pack
// (group_admin only, §7). Install is async (202 → poll GET installs/current).
// ===========================================================================

const PackKeyParam = z.object({ key: z.string() });

route({
  method: "get",
  path: "/v1/verticals",
  operationId: "listVerticals",
  summary: "List installable vertical packs (name/description/counts by kind)",
  permission: "onboarding.view",
  description:
    "Served read-only from the deployed packages/verticals bundle (platform-global, no tenant data — spec 02 §9.10).",
  responses: ok("The installable packs", VerticalPackListWrapper),
});
route({
  method: "get",
  path: "/v1/verticals/installs/current",
  operationId: "getCurrentVerticalInstall",
  summary: "The tenant's current pack install + per-kind disposition + seed links",
  permission: "onboarding.view",
  description:
    "Returns the active (or most recent) install row, the live per-kind disposition breakdown (seeded / snapshot / skipped-with-module, spec 02 §3.2), and the materialized pack_seed_links summary (§2.4). 404 PACK_INSTALL_NOT_FOUND when none exists.",
  responses: {
    ...ok("The current install + breakdown", VerticalPackInstallCurrentResponse),
    404: problemResponse("PACK_INSTALL_NOT_FOUND"),
  },
});
route({
  method: "get",
  path: "/v1/verticals/installs/current/update-preview",
  operationId: "previewVerticalPackUpdate",
  summary: "What the deployed pack would add, change and remove for this tenant",
  permission: "onboarding.install_pack",
  description:
    "D-343. Diffs the tenant's installed snapshot against the deployed bundle per object, and says what an apply would do to each: `applies` (added, or never materialized), `acceptable` (a changed object of a converging kind or a template, taken only when ticked), `customized` (the tenant made it its own; never touched), `adopt_only` (the kind never rewrites an existing row), `kept` (removed from the pack; never deleted) or `pending_module`. `update_available` compares digests, never versions. 404 PACK_INSTALL_NOT_FOUND, 409 PACK_INSTALL_IN_PROGRESS.",
  responses: {
    ...ok("The update preview", PackUpdatePreview),
    404: problemResponse("PACK_INSTALL_NOT_FOUND"),
    409: problemResponse("PACK_INSTALL_IN_PROGRESS, PACK_UNKNOWN"),
  },
});
route({
  method: "post",
  path: "/v1/verticals/installs/current/update",
  operationId: "applyVerticalPackUpdate",
  summary: "Apply the previewed pack update, taking the ticked changes",
  permission: "onboarding.install_pack",
  description:
    "D-343. Supersedes the current install and creates an `update` install row (status 'installing') the worker applies: added and missing objects are seeded, the ticked changes converge, nothing else is rewritten and nothing is deleted. Allowed on a live tenant (unlike a forced re-install). `available_digest` must be the preview's; a moved bundle is 409 PACK_UPDATE_STALE. 409 PACK_UP_TO_DATE when nothing would happen, 409 PACK_INSTALL_IN_PROGRESS, 422 PACK_UPDATE_NOT_ACCEPTABLE naming every accepted entry the preview does not list as acceptable.",
  request: { ...jsonBody(PackUpdateRequest) },
  responses: {
    ...created202("Accepted; poll GET installs/current for progress", VerticalPackInstall),
    404: problemResponse("PACK_INSTALL_NOT_FOUND"),
    409: problemResponse(
      "PACK_UPDATE_STALE, PACK_UP_TO_DATE, PACK_INSTALL_IN_PROGRESS, PACK_UNKNOWN",
    ),
    422: problemResponse("PACK_UPDATE_NOT_ACCEPTABLE"),
  },
});
route({
  method: "get",
  path: "/v1/verticals/{key}",
  operationId: "getVertical",
  summary: "Full pack content for preview (the VerticalPack shape)",
  permission: "onboarding.view",
  request: { params: PackKeyParam },
  responses: {
    ...ok("The full pack bundle", VerticalPackDetail),
    404: problemResponse("PACK_UNKNOWN"),
  },
});
route({
  method: "post",
  path: "/v1/verticals/{key}/install",
  operationId: "installVertical",
  summary: "Install (or force-switch) a vertical pack",
  permission: "onboarding.install_pack",
  description:
    "Creates the vertical_pack_installs row (status 'installing') with a deep-copied pack_snapshot (≤500 KB) and enqueues the in-process seed worker; poll GET installs/current. Idempotent seeding (adopt-or-create + pack_seed_links) makes a resumed install converge. 404 PACK_UNKNOWN, 409 PACK_ALREADY_INSTALLED (force=false), 422 PACK_SNAPSHOT_TOO_LARGE.",
  request: { params: PackKeyParam, ...jsonBody(PackInstallRequest) },
  responses: {
    ...created202("Accepted; poll GET installs/current for progress", VerticalPackInstall),
    404: problemResponse("PACK_UNKNOWN"),
    409: problemResponse("PACK_ALREADY_INSTALLED"),
    422: problemResponse("PACK_SNAPSHOT_TOO_LARGE"),
  },
});

// ===========================================================================
// go-live checklist + gate (spec 02 §2.10, §3.6, §5). The closed 19-item
// catalog is scored server-side; items whose owning module is not wired into
// the deployment report `pending` with `detail.blocked_on_module` — never a
// pass. Reads are onboarding.view; evaluation is a wizard mutation; waive /
// manual-set have their own permission codes (D-034); go-live is group_admin
// only and re-evaluates the gate inside a per-tenant advisory lock.
// ===========================================================================

const ItemKeyParam = z.object({ item_key: z.string() });

route({
  method: "get",
  path: "/v1/onboarding/checklist",
  operationId: "getOnboardingChecklist",
  summary: "The go-live checklist items + completeness score",
  permission: "onboarding.view",
  description:
    "Returns the stored checklist (one row per item of the closed §3.6 catalog) plus the cached score, the unmet blocking items and whether the go-live gate is currently satisfied. A pure read — it never evaluates; every item carries `evaluated_at` so staleness is visible, and POST /checklist/evaluate refreshes. `detail` carries the evidence: counts, the rule applied, and for an item whose owning module is not wired yet, `blocked_on_module` + the reason (status `pending`, never `passed`).",
  responses: ok("Checklist items + score", OnboardingChecklistResponse),
});
route({
  method: "post",
  path: "/v1/onboarding/checklist/evaluate",
  operationId: "evaluateOnboardingChecklist",
  summary: "Re-evaluate the whole checklist against the owning modules",
  permission: "onboarding.manage_wizard",
  description:
    "Re-runs every auto item by querying its owning module's service, upserts go_live_checklist_items, caches the score on the session and emits onboarding.checklist_evaluated. Waivers and manual attestations are preserved. Synchronous (200 with the refreshed checklist — the evaluator runs in-request; there is no worker to poll). Also runs automatically after a step completion, an import commit, a pack install and a plant-map commit (spec 02 §3.6 triggers). Refused once the tenant is live: the checklist at go-live is history (409 ALREADY_LIVE).",
  responses: {
    ...ok("The refreshed checklist", OnboardingChecklistResponse),
    409: problemResponse("ALREADY_LIVE — the session is live and the wizard is read-only"),
  },
});
route({
  method: "post",
  path: "/v1/onboarding/checklist/{item_key}/waive",
  operationId: "waiveChecklistItem",
  summary: "Waive a checklist item with a reason (>= 10 chars)",
  permission: "onboarding.checklist.waive",
  description:
    "Records a reasoned, audit-chained waiver (who + why; the >=10-char reason is also a DB CHECK) and emits onboarding.checklist_item_waived. Waiving a BLOCKING item additionally requires group_admin (spec 02 §2.10/§7) -> 403 BLOCKING_WAIVE_FORBIDDEN otherwise. A waived item counts toward the completeness score and satisfies the blocking gate.",
  request: { params: ItemKeyParam, ...jsonBody(ChecklistWaiveRequest) },
  responses: {
    ...ok("The checklist with the waiver applied", OnboardingChecklistResponse),
    403: problemResponse("BLOCKING_WAIVE_FORBIDDEN — blocking waives are group_admin only"),
    404: problemResponse("CHECKLIST_ITEM_NOT_FOUND — key outside the closed §3.6 catalog"),
    409: problemResponse("ALREADY_LIVE — the session is live and read-only"),
    422: problemResponse("WAIVE_REASON_TOO_SHORT — reason shorter than 10 characters"),
  },
});
route({
  method: "post",
  path: "/v1/onboarding/checklist/{item_key}/unwaive",
  operationId: "unwaiveChecklistItem",
  summary: "Remove a waiver and re-evaluate the item",
  permission: "onboarding.checklist.waive",
  description:
    "Drops the waiver (blocking items: group_admin only) and immediately re-evaluates the checklist, so the item returns to its true status rather than a stale verdict.",
  request: { params: ItemKeyParam },
  responses: {
    ...ok("The re-evaluated checklist", OnboardingChecklistResponse),
    403: problemResponse("BLOCKING_WAIVE_FORBIDDEN"),
    404: problemResponse("CHECKLIST_ITEM_NOT_FOUND"),
    409: problemResponse("ALREADY_LIVE"),
  },
});
route({
  method: "post",
  path: "/v1/onboarding/checklist/{item_key}/manual-set",
  operationId: "manualSetChecklistItem",
  summary: "Attest a MANUAL checklist item passed/failed",
  permission: "onboarding.checklist.manual_set",
  description:
    "Only `kind='manual'` items can be hand-set (auto items move by evaluation or by a reasoned waiver) -> 422 ITEM_NOT_MANUAL. `channels.floor_pwa_tested` may not be marked passed while its verifier (Module 04's pre-live test-step event, spec 02 §3.6) is not wired -> 422 CHECK_NOT_AVAILABLE; waiving it stays available. The attesting user is stamped into `detail`.",
  request: { params: ItemKeyParam, ...jsonBody(ChecklistManualSetRequest) },
  responses: {
    ...ok("The checklist with the attestation applied", OnboardingChecklistResponse),
    404: problemResponse("CHECKLIST_ITEM_NOT_FOUND"),
    409: problemResponse("ALREADY_LIVE"),
    422: problemResponse(
      "ITEM_NOT_MANUAL — the item is auto-evaluated / CHECK_NOT_AVAILABLE — the attestation's verifier module is not wired",
    ),
  },
});
route({
  method: "post",
  path: "/v1/onboarding/go-live",
  operationId: "onboardingGoLive",
  summary: "Activate the tenant (group_admin only)",
  permission: "onboarding.go_live",
  description:
    "Preconditions, in order: the session is `ready_for_review` — all four wizard steps completed (spec 02 §3.1, 422 PRECONDITIONS_UNMET) — then every BLOCKING item passed or waived AND completeness score >= 85 (spec 02 §3.6). The checklist is RE-EVALUATED inside a per-tenant advisory lock — the same lock the plant-map commit takes — so the gate cannot be beaten by a change landing between the check and the transition, and the transition itself is guarded by `status <> 'live'`: a concurrent double call transitions once and emits onboarding.went_live once, the loser gets 409 ALREADY_LIVE. On success the session becomes `live`, the wizard is read-only, and onboarding.went_live carries the score, pack, facility and product counts.",
  responses: {
    ...ok("The live session + final score", GoLiveResponse),
    403: problemResponse("PERMISSION_DENIED — go-live is group-scope group_admin only"),
    409: problemResponse("ALREADY_LIVE — the tenant is already live"),
    422: problemResponse(
      "PRECONDITIONS_UNMET — the wizard is not ready_for_review (extensions.incomplete_steps[]) / BLOCKING_ITEMS_UNMET — extensions.blocking_unmet[] / SCORE_BELOW_THRESHOLD — extensions.score, required, gap",
    ),
    503: problemResponse("RESOURCE_BUSY — another wizard mutation holds the lock; retry"),
  },
});

// ===========================================================================
// QR print kits — step 3 (spec 02 §2.8, §2.9, §3.4 pt 4, §5)
// ===========================================================================

const QrIdParam = z.object({ id: z.guid() });

route({
  method: "post",
  path: "/v1/qr-kits",
  operationId: "createQrKit",
  summary: "Queue a QR print batch (stations / stock-unit labels / location labels)",
  permission: "onboarding.generate_qr_kit",
  description:
    "Creates a point-in-time print batch and queues it for the render worker (202 → poll GET /v1/qr-kits/{id} for `ready` + `pdf_url`). Everything refusable is refused HERE, not later: the kind's owning module must be wired (422 QR_KIT_KIND_NOT_AVAILABLE, extensions name the module), `label_template_ref` must exist in the tenant's INSTALLED PACK SNAPSHOT and match the kind (422 QR_LABEL_TEMPLATE_NOT_FOUND / QR_LABEL_TEMPLATE_KIND_MISMATCH), the label must fit the page (422 QR_LABEL_TEMPLATE_TOO_LARGE), and a facility-scoped caller must name their own facility (422 QR_KIT_FACILITY_REQUIRED / 403). `params` accepts only the selection keys its kind understands — `department_ids`/`work_center_ids`, `location_ids`/`stock_unit_ids`, `location_ids` — and an unknown key is 422 QR_KIT_PARAMS_INVALID rather than a silent whole-facility print. Scan paths are read from the OWNING module (Module 16 for stations, Module 06 for units/locations), never minted here (§2.9): a missing payload is 422 QR_PAYLOAD_MISSING. A batch reaching `ready` emits onboarding.qr_kit_generated and satisfies the go-live checklist item channels.qr_kit_printed.",
  request: jsonBody(QrKitCreateRequest),
  responses: {
    ...created202("The queued batch (status `queued`)", QrPrintBatch),
    403: problemResponse("PERMISSION_DENIED — the facility is outside the caller's scope"),
    422: problemResponse(
      "QR_KIT_KIND_NOT_AVAILABLE / PACK_NOT_INSTALLED / QR_LABEL_TEMPLATE_NOT_FOUND / QR_LABEL_TEMPLATE_KIND_MISMATCH / QR_LABEL_TEMPLATE_TOO_LARGE / QR_KIT_PARAMS_INVALID / QR_KIT_SELECTION_TOO_LARGE / QR_KIT_FACILITY_REQUIRED",
    ),
  },
});

route({
  method: "get",
  path: "/v1/qr-kits",
  operationId: "listQrKits",
  summary: "List print batches, newest first",
  permission: "onboarding.generate_qr_kit",
  description:
    "Cursor pagination; `cursor` is the previous page's last batch id. Filters: `kind`, `status`, `facility_id`. A facility-scoped caller sees their facilities' batches plus group-wide (facility_id null) ones — `facility_id` is a data DESCRIPTOR with no RLS backstop (§2.8), so the narrowing is service-side. Reads carry the same grant as create by design: a batch's `params` enumerate work-center and location ids, so listing kits reads the plant map's shape.",
  request: {
    query: z.object({
      kind: z.string().optional(),
      status: z.string().optional(),
      facility_id: z.guid().optional(),
      cursor: z.guid().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: { ...ok("A page of batches", QrPrintBatchListResponse) },
});

route({
  method: "get",
  path: "/v1/qr-kits/{id}",
  operationId: "getQrKit",
  summary: "Batch detail + a presigned PDF link once ready",
  permission: "onboarding.generate_qr_kit",
  description:
    "`pdf_url` is a presigned GET minted per response, never a durable link: the file registry's 5-minute URL, served as an attachment, for a kit rendered from migration 0076 on (D-322), and a 15-minute URL for a kit rendered before it until the re-home moves it. It is null unless `status='ready'`, and null (rather than a 500) if object storage is momentarily unreachable. A `failed` batch carries a stable machine code in `error` (QR_LABEL_GLYPH_COVERAGE names the codepoints no bundled font can draw; QR_RENDER_FAILED is the catch-all). Out-of-scope ids are 404, indistinguishable from absent (§9.9).",
  request: { params: QrIdParam },
  responses: {
    ...ok("The batch", QrPrintBatch),
    404: problemResponse("QR_KIT_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/qr-kits/{id}/items",
  operationId: "listQrKitItems",
  summary: "The batch's FROZEN labels — what was printed, not what is true now",
  permission: "onboarding.generate_qr_kit",
  description:
    "A batch is a point-in-time snapshot (spec 02 §3.4): `label_lines` and each entity's `/q/{kind}/{uuid}` payload are frozen at render. This is how a user decides whether a sheet still matches the floor and needs regenerating — the sticker in the aisle says what THIS response says, not what the entity says today. Capped at 200 labels with `truncated` stating so; the PDF is the complete artefact. Ordered by page, then by the order they were laid out on it.",
  request: { params: QrIdParam },
  responses: {
    ...ok("The frozen labels", QrPrintItemListResponse),
    404: problemResponse("QR_KIT_NOT_FOUND"),
  },
});
